import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

const STORAGE_KEY = "cm:scrollMemory";
// Janela após input do usuário em que confiamos no scrollY reportado.
// Fora dela, eventos de scroll são tratados como clamp/reflow do browser e
// só atualizam a posição se forem MAIORES (high-water-mark).
const USER_INPUT_WINDOW_MS = 250;

function loadMap() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveMap(map) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // ignore
  }
}

function getScrollY() {
  if (typeof window === "undefined") return 0;
  const mainEl = document.querySelector("main");
  return Math.max(
    window.scrollY || 0,
    document.documentElement?.scrollTop || 0,
    document.body?.scrollTop || 0,
    mainEl?.scrollTop || 0,
  );
}

function applyScroll(y) {
  try {
    window.scrollTo(0, y);
  } catch {
    // ignore
  }
  if (typeof document !== "undefined") {
    if (document.documentElement) document.documentElement.scrollTop = y;
    if (document.body) document.body.scrollTop = y;
    const main = document.querySelector("main");
    if (main) main.scrollTop = y;
    document
      .querySelectorAll('[data-scroll-container="true"]')
      .forEach((el) => {
        el.scrollTop = y;
      });
  }
}

/**
 * ScrollToTop + Scroll Restoration robusto.
 *
 * Estratégia:
 *  - Listener global de scroll, wheel/touch/keydown.
 *  - Apenas atualizamos a posição salva (positionsRef[path]) quando:
 *      a) o evento ocorre dentro de USER_INPUT_WINDOW_MS após input do usuário
 *         (wheel / touchmove / keydown / pointerdown); OU
 *      b) o novo y é MAIOR que o já salvo (high-water-mark — protege contra
 *         clamps silenciosos do browser quando o scrollHeight do body encolhe).
 *  - Em PUSH/REPLACE: scrolla para o topo na nova rota.
 *  - Em POP: restaura o valor salvo.
 *  - Lock durante render quando pathname muda, para que eventos de scroll
 *    disparados pelo reflow da nova rota não corrompam positions.
 */
export function ScrollToTop() {
  const { pathname } = useLocation();
  const navType = useNavigationType();
  const positionsRef = useRef({});
  const activePathRef = useRef(pathname);
  const ignoreUntilRef = useRef(0);
  const lastUserInputAtRef = useRef(0);
  const debounceRef = useRef(null);

  // Lock síncrono no render quando há mudança de pathname.
  if (activePathRef.current !== pathname) {
    ignoreUntilRef.current = Date.now() + 600;
  }

  // Hidrata sessionStorage + scrollRestoration manual.
  useEffect(() => {
    positionsRef.current = { ...positionsRef.current, ...loadMap() };
    if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
      const prev = window.history.scrollRestoration;
      window.history.scrollRestoration = "manual";
      return () => {
        window.history.scrollRestoration = prev;
      };
    }
  }, []);

  // Listeners globais.
  useEffect(() => {
    if (typeof window === "undefined") return;

    const persistDebounced = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        saveMap(positionsRef.current);
      }, 100);
    };

    const markUserInput = () => {
      lastUserInputAtRef.current = Date.now();
    };

    const scrollHandler = () => {
      // Durante transição de rota, ignoramos completamente.
      if (Date.now() < ignoreUntilRef.current) return;

      const path = activePathRef.current;
      const y = getScrollY();
      const prev = positionsRef.current[path] || 0;

      const isUserDriven =
        Date.now() - lastUserInputAtRef.current < USER_INPUT_WINDOW_MS;

      if (isUserDriven) {
        // Confiamos no valor reportado: pode subir ou descer.
        positionsRef.current[path] = y;
      } else if (y > prev) {
        // Eventos não-driven só elevam (high-water-mark).
        positionsRef.current[path] = y;
      } else {
        return; // descarta clamp/reflow
      }
      persistDebounced();
    };

    // Eventos que indicam interação real de SCROLL do usuário.
    // (pointerdown/touchstart NÃO entram aqui — são taps/clicks, não scroll.
    // Incluí-los abre janela de 250ms onde clamps por reflow são tratados como
    // input válido, sobrescrevendo a posição salva da rota que está saindo.)
    window.addEventListener("wheel", markUserInput, { passive: true });
    window.addEventListener("touchmove", markUserInput, { passive: true });
    window.addEventListener("keydown", markUserInput, { passive: true });

    window.addEventListener("scroll", scrollHandler, {
      passive: true,
      capture: true,
    });
    document.addEventListener("scroll", scrollHandler, {
      passive: true,
      capture: true,
    });

    const onUnload = () => {
      const path = activePathRef.current;
      const y = getScrollY();
      const prev = positionsRef.current[path] || 0;
      positionsRef.current[path] = Math.max(prev, y);
      saveMap(positionsRef.current);
    };
    window.addEventListener("beforeunload", onUnload);

    return () => {
      window.removeEventListener("wheel", markUserInput);
      window.removeEventListener("touchmove", markUserInput);
      window.removeEventListener("keydown", markUserInput);
      window.removeEventListener("scroll", scrollHandler, { capture: true });
      document.removeEventListener("scroll", scrollHandler, { capture: true });
      window.removeEventListener("beforeunload", onUnload);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // useLayoutEffect: roda sincronamente após DOM commit, antes do paint.
  useLayoutEffect(() => {
    const prevPath = activePathRef.current;

    // Persiste o que temos da rota que está saindo no sessionStorage,
    // priorizando o maior valor (high-water-mark).
    if (prevPath && prevPath !== pathname) {
      const lastY = positionsRef.current[prevPath];
      if (typeof lastY === "number" && lastY > 0) {
        const map = loadMap();
        const merged = Math.max(map[prevPath] || 0, lastY);
        map[prevPath] = merged;
        saveMap(map);
        positionsRef.current = { ...positionsRef.current, ...map };
      }
    }

    activePathRef.current = pathname;

    let targetY = 0;
    if (navType === "POP") {
      const map = loadMap();
      targetY = Number(map[pathname]) || 0;
      positionsRef.current = { ...positionsRef.current, ...map };
    } else {
      // PUSH/REPLACE: reseta a memória dessa rota para futuras visitas.
      positionsRef.current[pathname] = 0;
    }

    applyScroll(targetY);
    const id1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => applyScroll(targetY));
    });
    const id2 = setTimeout(() => applyScroll(targetY), 120);
    const id3 = setTimeout(() => applyScroll(targetY), 320);
    const id4 = setTimeout(() => {
      applyScroll(targetY);
      ignoreUntilRef.current = Date.now();
    }, 500);

    return () => {
      cancelAnimationFrame(id1);
      clearTimeout(id2);
      clearTimeout(id3);
      clearTimeout(id4);
    };
  }, [pathname, navType]);

  return null;
}
